/**
 * Distributors operations view — Phase 2.2.a of the admin-frontend
 * decomposition.
 *
 * Owns the distributors tab on the Customers screen:
 *   - The list table (`#distributorsTbody`) + search.
 *   - The "Add/Edit distributor" form dialog.
 *   - The "View distributor" detail dialog with 5 nested tables (APs,
 *     sales, wallet entries, top-ups, payouts).
 *   - The manual top-up button on the detail dialog.
 *   - The per-row "Set PIN" action (distributors are created without a
 *     sign-in PIN, so somebody has to be able to give them one).
 *   - The `distributorCache` shared with the Access Points view (for
 *     populating distributor dropdowns there).
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - The Customers/Distributors tab toggle DOM refs (the tab UI is
 *     shared with the Customers view, slated for Phase 2.3).
 *   - `escapeHtml` and the formatters (the cluster has its own
 *     `formatMoney` since no other view uses it).
 *   - `openDialog` (generic dialog opener).
 *
 * Boot contract: `admin.html` loads this script after the Phase-1 utility
 * modules and after `views/tickets.js`. The IIFE registers
 * `window.adminViews.distributors`.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports.
 */

import { escapeHtml, formatDateTimeFull } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

let dom = null;
let mounted = false;
let distributorDetailId = null;
const distributorCache = { byId: new Map(), items: [] };

const helpers = { escapeHtml, openDialog };

  function resolveDom() {
    if (dom) return dom;
    dom = {
      tbody: document.getElementById('distributorsTbody'),
      dlg: document.getElementById('distributorDlg'),
      form: document.getElementById('distributorForm'),
      dlgTitle: document.getElementById('distributorDlgTitle'),
      detailDlg: document.getElementById('distributorDetailDlg'),
      detailTitle: document.getElementById('distributorDetailTitle'),
      detailSummary: document.getElementById('distributorDetailSummary'),
      aps: document.getElementById('distributorAps'),
      sales: document.getElementById('distributorSales'),
      wallet: document.getElementById('distributorWallet'),
      topups: document.getElementById('distributorTopups'),
      payouts: document.getElementById('distributorPayouts'),
      search: document.getElementById('distributorSearch'),
      addBtn: document.getElementById('addDistributorBtn'),
      manualTopupBtn: document.getElementById('distributorManualTopupBtn'),
    };
    return dom;
  }

  function formatMoney(amount, currency) {
    const a = Number(amount);
    if (!Number.isFinite(a)) return '';
    const cur = (currency || '').trim();
    return `${a.toFixed(2)}${cur ? ` ${cur}` : ''}`;
  }

  function fillTbody(tbody, cols, rows) {
    if (!tbody) return;
    tbody.innerHTML = '';
    if (!rows.length) {
      tbody.innerHTML = `<tr><td colspan="${cols}" style="color:var(--sub);">None</td></tr>`;
      return;
    }
    rows.forEach((trHtml) => {
      const tr = document.createElement('tr');
      tr.innerHTML = trHtml;
      tbody.appendChild(tr);
    });
  }

  async function ensureDistributorCache() {
    try {
      const data = await api.fetch(`${api.routes.adminDistributors}?limit=200&offset=0`, { method: 'GET' });
      const items = (data && data.items) ? data.items : [];
      distributorCache.items = items;
      distributorCache.byId = new Map(items.map((d) => [Number(d.id), d]));
    } catch (_) {
      distributorCache.items = [];
      distributorCache.byId = new Map();
    }
    return distributorCache;
  }

  function renderDistributors(items = []) {
    const d = resolveDom();
    const escape = helpers.escapeHtml;
    if (!d.tbody) return;
    d.tbody.innerHTML = '';
    if (!items.length) {
      d.tbody.innerHTML = '<tr><td colspan="9" style="color:var(--sub);">No distributors</td></tr>';
      return;
    }
    items.forEach((item, idx) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${idx + 1}</td>
        <td>${escape(item.name || '')}</td>
        <td>${escape(item.phone || '')}</td>
        <td>${escape(item.email || '')}</td>
        <td>${Number(item.commission_percent || 0).toFixed(1)}%</td>
        <td>${escape(formatMoney(item.balance, item.balance_currency))}</td>
        <td>${Number(item.ap_count || 0)}</td>
        <td>${escape(item.status || '')}${item.pin_set === false
          ? ' <span class="badge warn" title="This distributor cannot sign in until an administrator sets a PIN.">No PIN</span>'
          : ''}</td>
        <td>
          <button class="btn" data-dact="view" data-id="${item.id}">🔎 View</button>
          <button class="btn" data-dact="edit" data-id="${item.id}">✏️ Edit</button>
          <button class="btn" data-dact="pin" data-id="${item.id}">🔑 ${item.pin_set === false ? 'Set PIN' : 'Reset PIN'}</button>
        </td>`;
      d.tbody.appendChild(tr);
    });
  }

  async function loadDistributors(q = '') {
    const d = resolveDom();
    if (!d.tbody) {
      // eslint-disable-next-line no-console
      console.warn('distributors: #distributorsTbody missing — list will not load.');
      return;
    }
    const params = new URLSearchParams();
    params.set('limit', '200');
    params.set('offset', '0');
    if (q) params.set('q', q);
    try {
      const data = await api.fetch(`${api.routes.adminDistributors}?${params.toString()}`, { method: 'GET' });
      renderDistributors(data.items || []);
      await ensureDistributorCache();
    } catch (err) {
      renderDistributors([]);
      notify.showFlash(err.message, 'error');
    }
  }

  async function openDistributorDialog(id) {
    const d = resolveDom();
    if (!d.dlg || !d.form) {
      // eslint-disable-next-line no-console
      console.warn('distributors: distributor dialog elements missing.');
      return;
    }
    d.form.reset();
    d.form.id.value = id || '';
    if (d.dlgTitle) d.dlgTitle.textContent = id ? 'Edit distributor' : 'Add distributor';
    if (id) {
      const data = await api.fetch(api.routes.adminDistributor(id), { method: 'GET' });
      d.form.name.value = data.name || '';
      d.form.phone.value = data.phone || '';
      d.form.email.value = data.email || '';
      d.form.status.value = data.status || 'active';
      d.form.commission_percent.value = Number(data.commission_percent || 0);
      d.form.address.value = data.address || '';
      d.form.area_covered.value = data.area_covered || '';
    }
    helpers.openDialog(d.dlg);
  }

  async function openDistributorDetail(id, { show = true } = {}) {
    const d = resolveDom();
    const escape = helpers.escapeHtml;
    if (!d.detailDlg) {
      // eslint-disable-next-line no-console
      console.warn('distributors: #distributorDetailDlg missing.');
      return;
    }
    distributorDetailId = id;
    d.detailDlg.dataset.distributorId = String(id);
    const data = await api.fetch(api.routes.adminDistributor(id), { method: 'GET' });
    if (d.detailTitle) d.detailTitle.textContent = `${data.name || 'Distributor'} (${data.phone || ''})`;
    if (d.detailSummary) {
      d.detailSummary.textContent = `Status: ${data.status || ''} · Commission: ${Number(data.commission_percent || 0).toFixed(1)}% · Balance: ${formatMoney(data.balance, data.balance_currency)}`;
    }

    fillTbody(
      d.aps,
      4,
      (data.access_points || []).map((ap) => `
        <td>${escape(ap.ap_name || '')}</td>
        <td>${escape(ap.ap_mac || '')}</td>
        <td>${escape(ap.last_seen_ip || '')}</td>
        <td>${escape(ap.is_enabled ? 'enabled' : 'disabled')}</td>
      `),
    );

    try {
      const sales = await api.fetch(`${api.routes.adminDistributorSales(id)}?limit=50&offset=0`, { method: 'GET' });
      fillTbody(
        d.sales,
        7,
        (sales.items || []).map((s) => `
          <td>${escape(String(s.id || ''))}</td>
          <td>${escape(s.ap_name || '')}</td>
          <td>${escape(`${s.customer_name || ''} (${s.customer_phone || ''})`)}</td>
          <td>${escape(String(s.product_id || ''))}</td>
          <td>${escape(s.short_code || '')}</td>
          <td>${escape(s.status || '')}</td>
          <td>${escape(s.created_at ? formatDateTimeFull(s.created_at) : '')}</td>
        `),
      );
    } catch (_) {
      fillTbody(d.sales, 7, []);
    }

    try {
      const wallet = await api.fetch(`${api.routes.adminDistributorWallet(id)}?limit=50&offset=0`, { method: 'GET' });
      fillTbody(
        d.wallet,
        5,
        (wallet.items || []).map((w) => `
          <td>${escape(String(w.id || ''))}</td>
          <td>${escape(w.entry_type || '')}</td>
          <td>${escape(formatMoney(w.amount, w.currency))}</td>
          <td>${escape(`${w.ref_type || ''}:${w.ref_id || ''}`)}</td>
          <td>${escape(w.created_at ? formatDateTimeFull(w.created_at) : '')}</td>
        `),
      );
    } catch (_) {
      fillTbody(d.wallet, 5, []);
    }

    try {
      const topups = await api.fetch(`${api.routes.adminDistributorTopups(id)}?limit=50&offset=0`, { method: 'GET' });
      fillTbody(
        d.topups,
        6,
        (topups.items || []).map((t) => `
          <td>${escape(String(t.id || ''))}</td>
          <td>${escape(t.topup_type || '')}</td>
          <td>${escape(formatMoney(t.amount, t.currency))}</td>
          <td>${escape(t.status || '')}</td>
          <td>${escape(t.created_at ? formatDateTimeFull(t.created_at) : '')}</td>
          <td>${escape(t.completed_at ? formatDateTimeFull(t.completed_at) : '')}</td>
        `),
      );
    } catch (_) {
      fillTbody(d.topups, 6, []);
    }

    try {
      const payouts = await api.fetch(`${api.routes.adminDistributorPayouts(id)}?limit=50&offset=0`, { method: 'GET' });
      fillTbody(
        d.payouts,
        5,
        (payouts.items || []).map((p) => `
          <td>${escape(String(p.id || ''))}</td>
          <td>${escape(formatMoney(p.amount, p.currency))}</td>
          <td>${escape(p.wave_number || '')}</td>
          <td>${escape(p.status || '')}</td>
          <td>${escape(p.requested_at ? formatDateTimeFull(p.requested_at) : '')}</td>
        `),
      );
    } catch (_) {
      fillTbody(d.payouts, 5, []);
    }

    if (show) helpers.openDialog(d.detailDlg);
  }

  // Debounce helper (small enough to inline; admin.js's `debounce` is the
  // template).
  async function setDistributorPin(id) {
    // A distributor with no PIN cannot sign in at all, so this is the only way
    // back in for them — matched to the prompt-based manual top-up above
    // rather than adding another dialog to admin.html.
    const raw = prompt('New 4-digit sign-in PIN for this distributor:');
    if (raw == null) return;
    const pin = String(raw).trim();
    if (!/^\d{4}$/.test(pin)) {
      notify.showFlash('PIN must be 4 digits', 'error');
      return;
    }
    try {
      await api.fetch(api.routes.adminDistributorPin(id), {
        method: 'POST',
        body: JSON.stringify({ pin }),
      });
      notify.showFlash('PIN set — the distributor will be asked to change it at next sign-in');
      loadDistributors(resolveDom().search ? resolveDom().search.value.trim() : '');
    } catch (err) {
      notify.showFlash(err.message, 'error');
    }
  }

  function debounceLocal(fn, wait = 300) {
    let t = null;
    return (...args) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; fn(...args); }, wait);
    };
  }

  function mount() {
    if (mounted) return;
    const d = resolveDom();

    if (d.search) {
      d.search.addEventListener('input', debounceLocal(() => {
        loadDistributors(d.search.value.trim());
      }, 250));
    }
    if (d.addBtn) {
      d.addBtn.addEventListener('click', () => openDistributorDialog(null));
    }
    if (d.form) {
      d.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const id = d.form.id.value ? String(d.form.id.value).trim() : '';
        const payload = {
          name: d.form.name.value.trim(),
          phone: d.form.phone.value.trim(),
          email: d.form.email.value.trim() || null,
          status: d.form.status.value,
          commission_percent: Number(d.form.commission_percent.value || 0),
          address: d.form.address.value.trim() || null,
          area_covered: d.form.area_covered.value.trim() || null,
        };
        try {
          if (id) {
            await api.fetch(api.routes.adminDistributor(id), { method: 'PATCH', body: JSON.stringify(payload) });
          } else {
            await api.fetch(api.routes.adminDistributors, { method: 'POST', body: JSON.stringify(payload) });
          }
          d.dlg.close();
          notify.showFlash('Distributor saved');
          loadDistributors(d.search ? d.search.value.trim() : '');
          ensureDistributorCache();
        } catch (err) {
          notify.showFlash(err.message, 'error');
        }
      });
    }
    if (d.tbody) {
      d.tbody.addEventListener('click', async (e) => {
        const btn = e.target.closest('button[data-dact]');
        if (!btn) return;
        const id = btn.dataset.id;
        const act = btn.dataset.dact;
        if (act === 'edit') return openDistributorDialog(id);
        if (act === 'view') return openDistributorDetail(id);
        if (act === 'pin') return setDistributorPin(id);
      });
    }
    if (d.manualTopupBtn) {
      d.manualTopupBtn.addEventListener('click', async () => {
        const id = distributorDetailId || (d.detailDlg && d.detailDlg.dataset && d.detailDlg.dataset.distributorId);
        if (!id) return;
        const raw = prompt('Manual top-up amount (system currency):');
        if (raw == null) return;
        const amount = Number(String(raw).trim());
        if (!Number.isFinite(amount) || amount <= 0) {
          notify.showFlash('Invalid amount', 'error');
          return;
        }
        const note = prompt('Optional note for audit trail (leave blank if none):') || '';
        try {
          await api.fetch(api.routes.adminDistributorManualTopup(id), {
            method: 'POST',
            body: JSON.stringify({ amount: amount.toFixed(2), note }),
          });
          notify.showFlash('Manual top-up added');
          await openDistributorDetail(id, { show: false });
        } catch (err) {
          notify.showFlash(err.message, 'error');
        }
      });
    }
    mounted = true;
  }

export default {
  mount,
  refresh: loadDistributors,
  ensureCache: ensureDistributorCache,
  getCache: () => distributorCache,
  openDialog: openDistributorDialog,
  openDetail: openDistributorDetail,
  formatMoney,
};
